import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { X, AlertCircle, Loader2 } from "lucide-react";
import { Dialog, DialogContent, Button, ConfirmDialog } from "@tellescope/ui";
import { useVideoCalling, useMeetingOperations, useBackgroundEffect, VideoRoomView } from "@tellescope/video-calling";

interface VideoCallDialogProps {
  calendarEventId: string | null;
  eventTitle?: string;
  onClose: () => void;
}

/**
 * Full-page video call dialog for patients.
 *
 * - Opens automatically when calendarEventId is provided
 * - Mobile-first design with h-[100dvh] on mobile
 * - Prevents accidental close during active call
 * - Shows confirmation before leaving
 */
export function VideoCallDialog({ calendarEventId, eventTitle, onClose }: VideoCallDialogProps) {
  const { t } = useTranslation("videoCalling");
  const videoCalling = useVideoCalling();
  const { leaveMeeting, isJoining } = useMeetingOperations();

  // No backgroundImageURL in the portal — the Image effect never appears here (blur-only).
  useBackgroundEffect();

  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);

  const error = videoCalling.connectionError;
  const isConnected = videoCalling.connectionState === "connected";

  const handleClose = useCallback(() => {
    if (isConnected) {
      setShowLeaveConfirm(true);
    } else {
      leaveMeeting();
      onClose();
    }
  }, [isConnected, leaveMeeting, onClose]);

  const handleConfirmLeave = useCallback(() => {
    leaveMeeting();
    onClose();
  }, [leaveMeeting, onClose]);

  const handleEscapeKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (isConnected) {
        e.preventDefault();
        setShowLeaveConfirm(true);
      }
    },
    [isConnected]
  );

  const handleInteractOutside = useCallback(
    (e: Event) => {
      if (isConnected) {
        e.preventDefault();
      }
    },
    [isConnected]
  );

  return (
    <>
      <Dialog open={!!calendarEventId} modal>
        <DialogContent
          className="max-w-none w-full h-[100dvh] md:h-[90vh] md:w-[90vw] md:max-w-4xl p-0 gap-0 flex flex-col"
          showCloseButton={false}
          onEscapeKeyDown={handleEscapeKeyDown}
          onInteractOutside={handleInteractOutside}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-3 bg-gray-900 border-b border-gray-800">
            <h2 className="text-white font-medium truncate">{eventTitle ?? t("Video Call")}</h2>
            {!isConnected && (
              <Button variant="ghost" size="icon" onClick={handleClose} className="text-gray-400 hover:text-white">
                <X className="h-5 w-5" />
              </Button>
            )}
          </div>

          {/* Content - reuses VideoRoomView entirely */}
          <div className="flex-1 flex flex-col min-h-0">
            {error ? (
              <div className="flex-1 flex flex-col items-center justify-center p-6 bg-gray-900">
                <AlertCircle className="h-12 w-12 text-red-400 mb-4" />
                <p className="text-white text-center mb-4">{error}</p>
                <Button onClick={onClose}>{t("Close")}</Button>
              </div>
            ) : isJoining ? (
              <div className="flex-1 flex items-center justify-center bg-gray-900">
                <Loader2 className="h-8 w-8 animate-spin text-white" />
                <span className="ml-3 text-white">{t("Joining meeting...")}</span>
              </div>
            ) : (
              <VideoRoomView userRole="enduser" showEndForAll={false} />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Leave confirmation */}
      <ConfirmDialog
        open={showLeaveConfirm}
        onOpenChange={setShowLeaveConfirm}
        title={t("Leave call?")}
        description={t("Are you sure you want to leave this video call?")}
        confirmText={t("Leave")}
        cancelText={t("Stay")}
        onConfirm={handleConfirmLeave}
        variant="destructive"
      />
    </>
  );
}
